<template>
  <div>
    <v-card class="pa-5">
      <v-card-text>
        <v-card-title class="d-flex align-center">
          <h4>{{ $t('reports.inspectionCountByUnit') }}</h4>
          <v-spacer></v-spacer>

          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="filterData" v-on="on" v-bind="attrs" depressed color="primary" class="mr-2" min-width="40">
                  <v-icon small>fas fa-search</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.search') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="resetFilter" v-on="on" v-bind="attrs" depressed color="primary" class="mr-2" min-width="40">
                  <v-icon small>fa-solid fa-eraser</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.resetFilter') }}</span>
            </v-tooltip>

            <v-btn
              @click="download"
              :loading="loadingButton"
              :disabled="loadingTable || reportUnits.length === 0"
              color="primary"
              depressed
            >
              {{ $t('reports.downloadReport') }}
              <v-icon small class="ml-2">fas fa-download</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form v-model="valid" lazy-validation ref="form" @submit.prevent="filterData">
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.trim="filter.plate_number"
                  :label="$t('monitoring.plaque')"
                  :rules="rules.textNoSpecialCharsFilters"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.trim="filter.unit_number"
                  :label="$t('general.noEco')"
                  :rules="rules.textNoSpecialCharsFilters"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  v-model="filter.unit_type_id"
                  :items="unitTypes"
                  item-text="alias"
                  item-value="id"
                  :label="$t('general.unitType')"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  v-model="filter.questionnaire_id"
                  :items="tests"
                  item-text="name"
                  item-value="id"
                  :label="$t('reports.checklist')"
                  clearable
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  v-model="filter.owner"
                  :items="ownerTypes"
                  item-text="text"
                  item-value="value"
                  :label="$t('general.property')"
                  clearable
                  outlined
                  dense
                />
              </v-col>
              <v-col v-if="filter.dateRange" cols="12" sm="6" md="3">
                <v-menu v-model="menuStartDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.start_date"
                      :label="$t('general.startDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.start_date"
                    @input="handleStartDateSelection"
                  />
                </v-menu>
              </v-col>
              <v-col v-if="filter.dateRange" cols="12" sm="6" md="3">
                <v-menu v-model="menuEndDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.end_date"
                      :label="$t('general.endDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.end_date"
                    @input="menuEndDate = false"
                    :allowed-dates="allowedEndDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-checkbox
                  v-model="filter.dateRange"
                  :label="$t('general.dateRange')"
                  hide-details
                  class="mt-0"
                  color="primary"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-overlay absolute opacity="0" :value="loadingTable"/>
        <v-data-table
          :loading="loadingTable"
          :loading-text="$t('general.loading')"
          :headers="headers"
          :items="reportUnits"
          :server-items-length="totalItems"
          :options="optionsDataTable"
          :footer-props="{ 'items-per-page-options': [15, 50, 70, 100] }"
          @update:options="onOptionsChange"
          item-key="unit_id"
          show-expand
          class="elevation-1"
        >
          <template v-slot:item.plates:plate_number="{ item }">
            {{ formatPlates(item.plates) }}
          </template>
          <template v-slot:item.unit_number="{ item }">
            {{ item.unit_number || 'N/A' }}
          </template>
          <template v-slot:item.unit_types:alias="{ item }">
            {{ item.unit_types ? item.unit_types.alias : 'N/A' }}
          </template>
          <template v-slot:item.owner="{ item }">
            {{ translateOwner(item.owner) }}
          </template>
          <template v-slot:item.owner_name="{ item }">
            {{ item.owner_name || 'N/A' }}
          </template>
          <template v-slot:item.inspections_done_count="{ item }">
            <strong>{{ item.inspections_done_count || 0 }}</strong>
          </template>
          <template v-slot:item.inspections_damaged_count="{ item }">
            {{ item.inspections_damaged_count || 0 }}
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <inspection-count-by-unit-details :inspection-ids="item.inspection_ids || []"/>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import InspectionCountByUnitDetails from './inspectionCountByUnitDetails.vue'

const MAX_RANGE_DAYS = 29
const DEFAULT_SORT_KEY = 'inspections_done_count'

export default {
  name: 'inspectionCountByUnit',
  components: {
    InspectionCountByUnitDetails
  },
  data () {
    const filter = this.defaultFilter()

    return {
      valid: true,
      loadingTable: false,
      loadingButton: false,
      menuStartDate: false,
      menuEndDate: false,
      totalItems: 0,
      optionsDataTable: {
        itemsPerPage: 15,
        page: 1,
        sortBy: [DEFAULT_SORT_KEY],
        sortDesc: [true]
      },
      filter,
      // tests/get escribe tests, options y loadingTable en el contexto
      options: {
        totalItems: 0,
        page: 1
      },
      // Filtros con los que se consultó la tabla: la paginación, el orden y la descarga
      // usan estos, así el Excel siempre coincide con lo que se muestra en pantalla
      appliedFilter: { ...filter },
      reportUnits: [],
      unitTypes: [],
      tests: []
    }
  },
  watch: {
    'filter.dateRange' (val) {
      this.filter.start_date = val ? this.returnStartDate() : null
      this.filter.end_date = val ? this.returnEndDate() : null
    }
  },
  computed: {
    headers () {
      return [
        { text: '', value: 'data-table-expand', sortable: false },
        { text: this.$t('monitoring.plaque'), align: 'center', value: 'plates:plate_number' },
        { text: this.$t('general.noEco'), align: 'center', value: 'unit_number' },
        { text: this.$t('general.unitType'), align: 'center', value: 'unit_types:alias' },
        { text: this.$t('general.property'), align: 'center', value: 'owner' },
        { text: this.$t('general.owne'), align: 'center', value: 'owner_name' },
        { text: this.$t('inspections.count'), align: 'center', value: 'inspections_done_count' },
        { text: this.$t('inspections.damageInspections'), align: 'center', value: 'inspections_damaged_count' }
      ]
    },
    ownerTypes () {
      return [
        { text: this.$t('units.OWN'), value: 'OWN' },
        { text: this.$t('units.EXTERNAL'), value: 'EXTERNAL' }
      ]
    },
    rules () {
      return this.$validationRules()
    }
  },
  created () {
    this.$store.dispatch('unitTypes/getAll', { event: { context: this } })
    this.$store.dispatch('tests/get', { event: { context: this, item: { perPage: -1, page: 1 } } })
    // La primera carga la dispara v-data-table al emitir update:options en su inicialización
  },
  methods: {
    defaultFilter () {
      return {
        plate_number: null,
        unit_number: null,
        unit_type_id: null,
        questionnaire_id: null,
        owner: null,
        dateRange: true,
        start_date: this.returnStartDate(),
        end_date: this.returnEndDate()
      }
    },
    returnStartDate () {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substr(0, 10)
    },
    returnEndDate () {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substr(0, 10)
    },
    onOptionsChange (options) {
      this.optionsDataTable = options
      this.get()
    },
    resetFilter () {
      this.filter = this.defaultFilter()
      this.$refs.form.resetValidation()
      this.applyFilter()
    },
    filterData () {
      if (!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)
      this.applyFilter()
    },
    applyFilter () {
      this.appliedFilter = { ...this.filter }
      this.goToFirstPage()
    },
    goToFirstPage () {
      // Si la página cambia, la tabla emite update:options y dispara get()
      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable = { ...this.optionsDataTable, page: 1 }
    },
    filterParams () {
      const filter = this.appliedFilter

      return {
        plate_number: filter.plate_number || undefined,
        unit_number: filter.unit_number || undefined,
        unit_type_id: filter.unit_type_id || undefined,
        questionnaire_id: filter.questionnaire_id || undefined,
        owner: filter.owner || undefined,
        start_date: filter.start_date || undefined,
        end_date: filter.end_date || undefined
      }
    },
    get () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.optionsDataTable
      const hasSort = sortBy && sortBy.length > 0

      this.loadingTable = true
      this.$store.dispatch('reports/getInspectionCountByUnit', {
        event: {
          context: this,
          params: {
            ...this.filterParams(),
            type: 'json',
            sortKey: hasSort ? sortBy[0] : DEFAULT_SORT_KEY,
            ord: !hasSort || (sortDesc && sortDesc[0]) ? 'DESC' : 'ASC',
            perPage: itemsPerPage,
            page
          }
        }
      })
    },
    download () {
      this.loadingButton = true
      // El export incluye todo el resultado (sin paginar) y por requerimiento
      // siempre va de mayor a menor cantidad de inspecciones
      this.$store.dispatch('reports/getInspectionCountByUnitDoc', {
        event: {
          context: this,
          params: {
            ...this.filterParams(),
            type: 'download',
            sortKey: DEFAULT_SORT_KEY,
            ord: 'DESC'
          }
        }
      })
    },
    formatPlates (plates) {
      if (!plates || plates.length === 0) return 'N/A'
      return plates.map(plate => plate.plate_number).join(', ')
    },
    translateOwner (value) {
      const ownerMap = {
        OWN: this.$t('units.OWN'),
        EXTERNAL: this.$t('units.EXTERNAL')
      }
      return ownerMap[value] || 'N/A'
    },
    handleStartDateSelection (date) {
      this.filter.start_date = date
      this.filter.end_date = this.addDays(date, MAX_RANGE_DAYS)
      this.menuStartDate = false
    },
    allowedEndDates (date) {
      if (!this.filter.start_date) return true
      return date >= this.filter.start_date && date <= this.addDays(this.filter.start_date, MAX_RANGE_DAYS)
    },
    addDays (isoDate, days) {
      const date = new Date(`${isoDate}T00:00:00Z`)
      date.setUTCDate(date.getUTCDate() + days)
      return date.toISOString().substr(0, 10)
    },
    getDayStyle (date) {
      if (this.allowedEndDates(date)) return null
      return { backgroundColor: 'rgba(0, 0, 0, 0.1)', color: 'gray' }
    }
  }
}
</script>
